import React, { type FunctionComponent, useMemo, useState, useEffect } from 'react'
import { Alert } from '@retailer-platform/shared-components'
import { useDomainMessages } from '../../../utils/domain/intl.ts'
import {
  Input,
  PropertyContainer,
  PropertyRow,
  PropertyTitle,
} from '../../shared-styled-components/SharedStyledComponents'
import {
  AttributeManagementNextStepTypeEnum,
  type PipelineStep,
  type PipelineStepNextStepsRule,
} from '../../../types/attributeManagement.types.ts'
import { Spacer } from '../../../utils/common.styles.tsx'

interface Props {
  step: PipelineStep
  onChange: (step: PipelineStep) => void
}

const MAX_NUM = 10
const MIN_NUM = 1

// Component to handle numeric input with proper support for intermediate states like "-" and ""
const NumericInput: FunctionComponent<{
  value: number
  onChange: (value: number) => void
  error?: boolean
}> = ({ value, onChange, error = false }) => {
  const [displayValue, setDisplayValue] = useState(String(value))

  // Sync display value when prop value changes externally
  useEffect(() => {
    setDisplayValue(String(value))
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    setDisplayValue(inputValue)

    // Allow intermediate states like "-" or "" without updating parent
    if (inputValue === '' || inputValue === '-') {
      onChange(0)
    } else {
      const parsed = Number.parseFloat(inputValue)
      if (!Number.isNaN(parsed)) {
        onChange(parsed)
      }
    }
  }

  return <Input type={'number'} value={displayValue} onChange={handleChange} error={error} />
}

export const PipelineFormStepControlNumericRanges: FunctionComponent<Props> = ({
  step,
  onChange,
}) => {
  const i18n = useDomainMessages({
    range:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlNumericRanges.range',
    numberOfRanges:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlNumericRanges.numberOfRanges',
    rangeDescriptionHelp:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.rangeDescription.help',
    valueField:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlNumericRanges.valueField',
  })

  const nextStepsRule: PipelineStepNextStepsRule = useMemo(
    () => ({
      type: AttributeManagementNextStepTypeEnum.NumericRange,
      ranges: step?.nextStepsRule?.ranges ?? [
        {
          rangeHigh: 1,
          rangeLow: 0,
          nextPipelineStep: '',
        },
      ],
      defaultNextPipelineStep: undefined,
      possibleNextSteps: step?.nextStepsRule?.possibleNextSteps ?? [],
      valueField: step?.nextStepsRule?.valueField ?? '',
    }),
    [
      step?.nextStepsRule?.possibleNextSteps,
      step?.nextStepsRule?.ranges,
      step?.nextStepsRule?.valueField,
    ]
  )

  // Validate ranges for errors
  const rangeErrors = useMemo(() => {
    const errors: Array<{ lowError: boolean; highError: boolean }> = []
    const ranges = nextStepsRule.ranges ?? []

    ranges.forEach((range, index) => {
      let lowError = false
      let highError = false

      // Check if range is invalid (low > high)
      if (range.rangeLow > range.rangeHigh) {
        lowError = true
        highError = true
      }

      // Check if this range overlaps with any other range
      for (let i = 0; i < ranges.length; i++) {
        if (i === index) continue

        const otherRange = ranges[i]
        // Two ranges [a, b] and [c, d] overlap if: a <= d AND c <= b
        const overlaps =
          range.rangeLow <= otherRange.rangeHigh && otherRange.rangeLow <= range.rangeHigh

        if (overlaps) {
          // Mark the specific bounds that are causing the overlap
          if (range.rangeLow >= otherRange.rangeLow && range.rangeLow <= otherRange.rangeHigh) {
            lowError = true
          }
          if (range.rangeHigh >= otherRange.rangeLow && range.rangeHigh <= otherRange.rangeHigh) {
            highError = true
          }
        }
      }

      errors.push({ lowError, highError })
    })

    return errors
  }, [nextStepsRule.ranges])

  return (
    <>
      <PropertyContainer style={{ flexDirection: 'column' }}>
        <PropertyRow>
          <PropertyTitle>{i18n.valueField}</PropertyTitle>
          <Input
            type={'text'}
            value={nextStepsRule.valueField ?? ''}
            error={!nextStepsRule.valueField?.trim()}
            onChange={e => {
              onChange({ ...step, nextStepsRule: { ...nextStepsRule, valueField: e.target.value } })
            }}
          />
        </PropertyRow>
        <PropertyRow>
          <PropertyTitle>{i18n.numberOfRanges}</PropertyTitle>
          <Input
            type={'number'}
            value={nextStepsRule.ranges?.length ?? 1}
            onChange={e => {
              const amount = Math.min(MAX_NUM, Math.max(MIN_NUM, Number.parseInt(e.target.value)))

              const ranges: { rangeLow: number; rangeHigh: number; nextPipelineStep: string }[] = []

              for (let i = 0; i < amount; i++) {
                ranges.push(
                  nextStepsRule?.ranges?.[i] ?? {
                    rangeHigh: 0,
                    rangeLow: 0,
                    nextPipelineStep: '',
                  }
                )
              }
              onChange({
                ...step,
                nextStepsRule: {
                  ...nextStepsRule,
                  ranges: ranges,
                },
              })
            }}
          />
        </PropertyRow>
        <PropertyRow css={{ maxWidth: '300px', minWidth: '100%' }}>
          <Alert variant={'info'}>{i18n.rangeDescriptionHelp}</Alert>
        </PropertyRow>
        {(nextStepsRule.ranges ?? []).map((range, index) => (
          <PropertyRow key={`${index}`}>
            <PropertyTitle>
              {i18n.range} {index + 1}:
            </PropertyTitle>
            <Spacer />
            <NumericInput
              value={range.rangeLow ?? 0}
              error={rangeErrors[index]?.lowError}
              onChange={value => {
                if (!nextStepsRule.ranges) return
                nextStepsRule.ranges[index].rangeLow = value
                onChange({
                  ...step,
                  nextStepsRule: { ...nextStepsRule, ranges: [...nextStepsRule.ranges] },
                })
              }}
            />
            <NumericInput
              value={range.rangeHigh ?? 0}
              error={rangeErrors[index]?.highError}
              onChange={value => {
                if (!nextStepsRule.ranges) return
                nextStepsRule.ranges[index].rangeHigh = value
                onChange({
                  ...step,
                  nextStepsRule: { ...nextStepsRule, ranges: [...nextStepsRule.ranges] },
                })
              }}
            />
          </PropertyRow>
        ))}
      </PropertyContainer>
    </>
  )
}
